// Who is an admin, on the Admin page (RFC-050 "Admins by configuration",
// byj-1wv.72): the admins configured where Share is deployed, and the people
// one of them made an admin for a time — whose, until when, by whom, and
// whether it holds. A configured admin makes another person who has signed
// in an admin until a day at most a month on, and ends it, asked first; a
// delegate sees the list and is told only a configured admin gives or ends
// admin. Each is mailed to the delegate and the other configured admins.
// As /shares lists shares (byj-1wv.51.13): the configured and the delegated
// as rows under their headings, End behind a delegate's ⋯, and making one a
// short form over the page opened beside the tab's lead, the person
// found as they are typed (components/PersonField).
import { useCallback, useEffect, useState } from "react";
import { isAddress } from "../address";
import { api, type AdminDelegate, type AdminsList } from "../api";
import { t, dayInput, whenMinute } from "../i18n";
import { useConfirm } from "./Confirm";
import { FormDialog } from "./FormDialog";
import { PersonField } from "./PersonField";
import { RowMenu, type MenuItem } from "./RowMenu";
import { EmptyRows, Grouped, initialOf } from "./Rows";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** The calls the section makes, so a test can supply its own. */
export type AdminsApi = Pick<typeof api, "adminAdmins" | "delegateAdmin" | "endAdminDelegation" | "adminUsers">;

/** The default days a delegation runs. */
export const defaultDelegationDays = 7;

/** A date input's value, `days` from today where the admin is. */
export function dayFromToday(days: number, now = new Date()): string {
  return dayInput(new Date(now.getFullYear(), now.getMonth(), now.getDate() + days));
}

/** A date input's day as the end of that day where the admin is: a grant's end, on the Machines and Demo accounts tabs. */
export function endOfDay(day: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  return new Date(`${day}T23:59:59`).toISOString();
}

/**
 * When a delegation picked to `day` ends: the end of that day where the
 * admin is, but never past the relay's longest, so the last day it offers is
 * never refused.
 */
export function delegationEnd(day: string, maxDays: number, now = new Date()): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  const end = new Date(`${day}T23:59:59`);
  const most = new Date(now.getTime() + maxDays * 24 * 3600_000 - 60_000);
  if (!(end.getTime() > now.getTime())) return undefined;
  return (end > most ? most : end).toISOString();
}


/** A configured admin's row: their address, and that it is set where Share is deployed. */
function ConfiguredRow({ email }: { email: string }) {
  return (
    <li className="share-row admin-row is-live" data-testid="configured-admin">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(email)}
      </span>
      <div className="row-main">
        <span className="row-title">{email}</span>
      </div>
      <div className="row-meta">
        <span className="chip">{t("admin:adminsConfigured")}</span>
      </div>
      <RowMenu label={t("shares:moreFor", { title: email })} items={[]} />
    </li>
  );
}

/** A delegate's row: until when, by whom, whether it holds; End behind ⋯, for a configured admin. */
function DelegateRow({ d, canEnd, onEnd }: { d: AdminDelegate; canEnd: boolean; onEnd: (d: AdminDelegate) => void }) {
  const sub = [d.name, t("admin:adminsUntil", { w: whenMinute(d.expires_at) }), d.granted_by && t("admin:adminBy", { email: d.granted_by })].filter(Boolean);
  const menu: MenuItem[] = canEnd ? [{ label: t("admin:adminsEnd"), onSelect: () => onEnd(d), danger: true }] : [];
  return (
    <li className={`share-row admin-row ${d.live ? "is-live" : "is-ended"}`} data-testid="delegate">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(d.email)}
      </span>
      <div className="row-main">
        <span className="row-title">{d.email}</span>
        <div className="row-sub">{sub.join(" · ")}</div>
      </div>
      <div className="row-meta">
        {d.live ? <span className="chip">{t("admin:adminsDelegated")}</span> : <span className="chip chip-warn">{t("admin:adminsNotHolding")}</span>}
      </div>
      <RowMenu label={t("shares:moreFor", { title: d.email })} items={menu} />
    </li>
  );
}

/**
 * Making someone an admin until a day, a short form over the page
 * (components/FormDialog): the person found as they are typed — only one
 * who has signed in can be one — and the last day. The dialog picks; the
 * page makes them one.
 */
function DelegateDialog({ maxDays, find, onSubmit, onClose }: {
  maxDays: number;
  find: AdminsApi["adminUsers"];
  onSubmit: (email: string, end: string) => Promise<void>;
  onClose: () => void;
}) {
  const [email, setEmail] = useState("");
  const [day, setDay] = useState(() => dayFromToday(defaultDelegationDays));
  const end = delegationEnd(day, maxDays);
  return (
    <FormDialog
      id="delegate-dialog"
      testid="delegate-admin"
      title={t("admin:adminsMakeTitle")}
      submitLabel={t("admin:adminsMake")}
      canSubmit={isAddress(email) && !!end}
      onSubmit={() => onSubmit(email.trim(), end!)}
      onClose={onClose}
    >
      <PersonField find={find} label={t("admin:adminEmail")} ariaLabel={t("admin:adminsWho")} value={email} onChange={setEmail} onPick={(u) => setEmail(u.email)} />
      <label className="field">
        {t("admin:adminsUntilLabel")}
        <input
          type="date"
          value={day}
          min={dayFromToday(0)}
          max={dayFromToday(maxDays - 1)}
          onChange={(e) => setDay(e.target.value)}
          aria-label={t("admin:adminsUntilLabel")}
        />
      </label>
      <p className="muted small flush">{t("admin:adminsMakeNote", { days: maxDays })}</p>
    </FormDialog>
  );
}

/** A row of the list: a configured admin by address, or a delegate. */
type Who = { email: string; d?: AdminDelegate };

export function Admins({ client = api, until, onCount }: { client?: AdminsApi; until?: string; onCount?: (n: number) => void }) {
  const [list, setList] = useState<AdminsList | null>(null);
  const [making, setMaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [ask, confirmDialog] = useConfirm();
  const reload = useCallback(async () => {
    try {
      const l = await client.adminAdmins();
      setList(l);
      onCount?.(l.configured.length + l.delegates.length);
    } catch (err) {
      setError(errText(err));
    }
  }, [client, onCount]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const maxDays = list?.max_days ?? 30;
  const close = () => setMaking(false);
  // made, said so; refused, said in the dialog, which stays open
  const make = async (email: string, end: string) => {
    const r = await client.delegateAdmin(email, end);
    setDone(t("admin:adminsMade", { email: r.delegate.email, until: whenMinute(r.delegate.expires_at) }));
    setError(null);
    setMaking(false);
    await reload();
  };
  const end = async (d: AdminDelegate) => {
    if (!(await ask(t("admin:adminsEndAsk", { email: d.email }), { action: t("admin:adminsEnd"), danger: true }))) return;
    try {
      await client.endAdminDelegation(d.user_id);
      setDone(t("admin:adminsEnded", { email: d.email }));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
    await reload();
  };

  const makeAction = list?.can_delegate ? (
    <button
      className="primary"
      aria-haspopup="dialog"
      aria-expanded={making}
      aria-controls={making ? "delegate-dialog" : undefined}
      onClick={() => {
        setDone(null);
        setMaking(true);
      }}
    >
      {t("admin:adminsMake")}
    </button>
  ) : undefined;
  return (
    <div data-testid="admins">
      {/* the tab's one way to add, beside what the tab is */}
      <div className="panel-head">
        <p className="muted small panel-lead">{t("admin:adminsLead", { days: maxDays })}</p>
        {makeAction}
      </div>
      {until && (
        <p className="warn small" data-testid="admin-delegated">
          {t("admin:adminsYoursUntil", { w: whenMinute(until) })}
        </p>
      )}
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {done && (
        <p className="ok-text" role="status">
          {done}
        </p>
      )}
      {list === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : (
        <>
          <Grouped<Who>
            id="admins"
            headed
            groups={[
              { title: t("admin:adminsConfiguredGroup"), items: list.configured.map((email) => ({ email })) },
              { title: t("admin:adminsDelegatedGroup"), items: list.delegates.map((d) => ({ email: d.email, d })), none: <EmptyRows text={t("admin:adminsNoDelegates")} /> },
            ]}
            row={(x) => (x.d ? <DelegateRow key={x.d.user_id} d={x.d} canEnd={list.can_delegate} onEnd={(d) => void end(d)} /> : <ConfiguredRow key={x.email} email={x.email} />)}
          />
          {!list.can_delegate && <p className="muted small">{t("admin:adminsOnlyConfigured")}</p>}
        </>
      )}
      {making && <DelegateDialog maxDays={maxDays} find={client.adminUsers} onSubmit={make} onClose={close} />}
      {confirmDialog}
    </div>
  );
}
